import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom } from 'rxjs';
import { Address } from 'src/app/admin-api/model/address';
import { TypedError } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { PersonAddressService } from 'src/app/services/person-address.service';
import {
  INVALID_CEP_MESSAGE,
  SUBSCRIBER_CEP_FREIGHT_NOTE,
  apiErrorMessage,
  buildPersonAddressUpdateRequest,
  normalizePostalCode,
  postalCodeValidator,
  viaCepAddressPatch
} from 'src/app/services/person-address.util';
import { ViaCepService } from 'src/app/services/viacep.service';

interface AddressEditFormControls {
  addressType: FormControl<Address.AddressTypeEnum | null>;
  city: FormControl<string>;
  country: FormControl<string>;
  neighborhood: FormControl<string>;
  phone: FormControl<string>;
  postalCode: FormControl<string>;
  recipientName: FormControl<string>;
  state: FormControl<string>;
  street: FormControl<string>;
  streetExtension: FormControl<string>;
  streetNumber: FormControl<string>;
}

@Component({
  selector: 'app-address-edit-modal',
  templateUrl: './address-edit-modal.component.html',
  styleUrl: './address-edit-modal.component.scss'
})
export class AddressEditModalComponent {
  private config = inject(DynamicDialogConfig);
  private personAddressService = inject(PersonAddressService);
  private viaCepService = inject(ViaCepService);
  ref = inject(DynamicDialogRef);

  address: Address = this.config.data?.address;
  personId: number = this.config.data?.personId ?? this.address?.personId;
  subscriberFreightNote = this.address?.subscriber
    ? SUBSCRIBER_CEP_FREIGHT_NOTE
    : '';
  invalidCepMessage = INVALID_CEP_MESSAGE;
  cepLookupMessage = '';
  addressTypeOptions: Array<{ label: string; value: Address.AddressTypeEnum }> =
    [
      { label: 'Cobrança', value: Address.AddressTypeEnum.BILLING },
      { label: 'Entrega', value: Address.AddressTypeEnum.SHIPPING }
    ];

  form = createAddressEditForm(this.address);

  invalidControl(control: keyof AddressEditFormControls): boolean {
    const field = this.form.controls[control];
    return field.invalid && (field.dirty || field.touched);
  }

  async onPostalCodeComplete(): Promise<void> {
    if (!this.form.controls.postalCode.dirty) return;
    const digits = normalizePostalCode(this.form.controls.postalCode.value);
    if (!digits || digits.length !== 8) return;
    await this.lookupCep(digits);
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const addressId = this.address?.addressId;
    if (this.personId == null || addressId == null) {
      AppDialogService.showErrorDialog({
        message: 'Endereço inválido.'
      });
      return;
    }

    const body = buildPersonAddressUpdateRequest(this.form.getRawValue());
    const previousShippingPrice = this.address.shippingPrice;
    LoaderService.showLoader();
    try {
      const saved = await lastValueFrom(
        this.personAddressService.update(this.personId, addressId, body)
      );
      this.ref.close({ address: saved, previousShippingPrice });
    } catch (error) {
      this.showUpdateError(error as TypedError);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private async lookupCep(digits: string): Promise<void> {
    this.cepLookupMessage = '';
    LoaderService.showLoader();
    try {
      const data = await lastValueFrom(this.viaCepService.lookup(digits));
      const patch = viaCepAddressPatch(data);
      if (!patch) {
        this.cepLookupMessage =
          'Não foi possível consultar o CEP. Preencha o endereço manualmente.';
        return;
      }
      this.form.patchValue(patch);
    } catch {
      this.cepLookupMessage =
        'Não foi possível consultar o CEP. Preencha o endereço manualmente.';
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private showUpdateError(error: TypedError): void {
    const message = apiErrorMessage(error);
    if (error?.status === 403) {
      AppDialogService.showInfoDialog({ header: 'Erro', message });
      return;
    }
    AppDialogService.showErrorDialog(error, false, message);
  }
}

export function createAddressEditForm(
  address: Address | undefined
): FormGroup<AddressEditFormControls> {
  return new FormGroup<AddressEditFormControls>({
    postalCode: new FormControl(formatPostalCode(address?.postalCode), {
      nonNullable: true,
      validators: [Validators.required, postalCodeValidator]
    }),
    street: new FormControl(address?.street ?? '', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    streetNumber: new FormControl(address?.streetNumber ?? '', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    streetExtension: new FormControl(address?.streetExtension ?? '', {
      nonNullable: true
    }),
    neighborhood: new FormControl(address?.neighborhood ?? '', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    city: new FormControl(address?.city ?? '', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    state: new FormControl(address?.state ?? '', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    country: new FormControl(address?.country ?? '', { nonNullable: true }),
    addressType: new FormControl<Address.AddressTypeEnum | null>(
      address?.addressType ?? null,
      { validators: [Validators.required] }
    ),
    recipientName: new FormControl(address?.recipientName ?? '', {
      nonNullable: true
    }),
    phone: new FormControl(address?.phone ?? '', { nonNullable: true })
  });
}

function formatPostalCode(value: string | null | undefined): string {
  const digits = normalizePostalCode(value);
  if (!digits) return '';
  if (digits.length !== 8) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}
